import { useState, useEffect, useCallback, useRef } from "react";
import { useCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import { buildPrimaryFetchInit, resolvePrimaryEnvironmentHttpUrl } from "../environments/primary";
import { useCurrentForgeWorkspaceState } from "../workspace/currentForgeWorkspaceStore";

export interface GitHubRepo {
  readonly full_name: string;
  readonly html_url: string;
  readonly default_branch: string;
  readonly private: boolean;
  readonly description: string | null;
  readonly updated_at: string | null;
  readonly language: string | null;
  readonly owner_type: "personal" | "org";
  readonly org_name?: string;
  readonly installation_id?: number;
  readonly source?: "oauth" | "app";
  readonly access_label?: string;
}

interface GitHubStatusResponse {
  connected: boolean;
  github_login?: string;
  user_repo_access_usable?: boolean;
  code?: string;
}

interface GitHubReposResponse {
  repos: GitHubRepo[];
  github_connected: boolean;
  user_repo_access_usable?: boolean;
  warnings?: readonly string[];
  error?: string;
  code?: string;
}

type GitHubReposLoadResult =
  | { readonly kind: "loaded"; readonly data: GitHubReposResponse }
  | { readonly kind: "reauth"; readonly message: string }
  | { readonly kind: "error"; readonly message: string };

type GitHubStatusLoadResult =
  | { readonly kind: "connected"; readonly connected: boolean; readonly githubLogin: string | null }
  | { readonly kind: "reauth"; readonly message: string }
  | { readonly kind: "error" };

export function buildGitHubReposUrl(
  searchTerm: string | undefined,
  workspaceId: string | null,
): string {
  const base = resolvePrimaryEnvironmentHttpUrl("/.well-known/forge/github/repos");
  const params = new URLSearchParams();
  const trimmedSearch = searchTerm?.trim();
  if (trimmedSearch) params.set("search", trimmedSearch);
  if (workspaceId) params.set("organization_id", workspaceId);
  const query = params.toString();
  return query.length > 0 ? `${base}?${query}` : base;
}

function classifyGitHubReposResponse(
  response: Response,
  data: GitHubReposResponse,
): GitHubReposLoadResult {
  const requiresReauth = response.status === 401 || data.code === "CMMD_REAUTH_REQUIRED";
  if (requiresReauth) {
    return { kind: "reauth", message: data.error ?? "Sign back in to CMMD to continue." };
  }
  if (!response.ok) {
    return { kind: "error", message: data.error ?? `Failed to load repos (${response.status})` };
  }
  return { kind: "loaded", data };
}

async function loadGitHubRepos(
  searchTerm: string | undefined,
  workspaceId: string | null,
): Promise<GitHubReposLoadResult> {
  try {
    const response = await fetch(
      buildGitHubReposUrl(searchTerm, workspaceId),
      buildPrimaryFetchInit({ credentials: "include" }),
    );
    const data = response.ok
      ? ((await response.json()) as GitHubReposResponse)
      : ((await response.json().catch(() => ({}))) as GitHubReposResponse);
    return classifyGitHubReposResponse(response, data);
  } catch (error) {
    return {
      kind: "error",
      message: error instanceof Error ? error.message : "Failed to load repos",
    };
  }
}

function classifyGitHubStatusResponse(
  response: Response,
  data: GitHubStatusResponse,
): GitHubStatusLoadResult {
  const requiresReauth = response.status === 401 || data.code === "CMMD_REAUTH_REQUIRED";
  if (requiresReauth) {
    return { kind: "reauth", message: "Sign back in to CMMD to continue." };
  }
  if (!response.ok) {
    return { kind: "error" };
  }
  return { kind: "connected", connected: data.connected, githubLogin: data.github_login ?? null };
}

async function loadGitHubStatus(): Promise<GitHubStatusLoadResult> {
  try {
    const url = resolvePrimaryEnvironmentHttpUrl("/.well-known/forge/github/status");
    const response = await fetch(url, buildPrimaryFetchInit({ credentials: "include" }));
    const data = response.ok
      ? ((await response.json()) as GitHubStatusResponse)
      : ((await response.json().catch(() => ({}))) as GitHubStatusResponse);
    return classifyGitHubStatusResponse(response, data);
  } catch {
    return { kind: "error" };
  }
}

export function getGitHubRepoAccessLabel(repo: GitHubRepo): string {
  if (repo.access_label?.trim()) return repo.access_label;
  if (repo.owner_type === "org" && repo.org_name) return repo.org_name;
  return "Personal";
}

interface UseGitHubReposResult {
  repos: GitHubRepo[];
  github_connected: boolean;
  github_login: string | null;
  warnings: readonly string[];
  loading: boolean;
  error: string | null;
  requiresCmmdAuth: boolean;
  refetch: () => void;
}

export function useGitHubRepos(
  search?: string,
  options: {
    readonly enabled?: boolean;
    /** Scope repository access to the owning CMMD workspace instead of the active workspace. */
    readonly organizationId?: string | null;
  } = {},
): UseGitHubReposResult {
  const enabled = options.enabled ?? true;
  const workIntakeWorkspaceId = useCmmdWorkIntakeSnapshot().workspace?.id ?? null;
  // CMMD-Center/forge#3572: the confirmed shell workspace wins over the
  // work-intake's own guessed `workspace` when the account has one. Both
  // this hook and `currentForgeWorkspaceStore` use CMMD organization ids;
  // this hook's own contract calls it a string, so it stringifies here
  // rather than changing that contract.
  const { workspaceId: currentForgeWorkspaceId } = useCurrentForgeWorkspaceState();
  const activeWorkspaceId =
    currentForgeWorkspaceId !== null ? String(currentForgeWorkspaceId) : workIntakeWorkspaceId;
  const workspaceId =
    options.organizationId === undefined ? activeWorkspaceId : options.organizationId;
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [github_connected, setGithubConnected] = useState(false);
  const [github_login, setGithubLogin] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<readonly string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requiresCmmdAuth, setRequiresCmmdAuth] = useState(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fetchCountRef = useRef(0);
  // CMMD-Center/forge#3572 review: a fetch or poll started for one workspace
  // must not write state or keep polling once the user has switched to
  // another. Every async continuation below captures `workspaceId` when it
  // starts and checks it against this ref -- always the latest value --
  // before writing state or scheduling more work.
  const workspaceIdRef = useRef(workspaceId);
  useEffect(() => {
    workspaceIdRef.current = workspaceId;
  }, [workspaceId]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const fetchRepos = useCallback(
    async (searchTerm?: string) => {
      const id = ++fetchCountRef.current;
      setLoading(true);
      setError(null);
      setWarnings([]);
      setRequiresCmmdAuth(false);
      const result = await loadGitHubRepos(searchTerm, workspaceId);
      if (id !== fetchCountRef.current) return;
      if (result.kind === "loaded") {
        setRepos(result.data.repos ?? []);
        setGithubConnected(result.data.github_connected);
        setWarnings(result.data.warnings ?? []);
      }
      if (result.kind === "reauth") setRequiresCmmdAuth(true);
      if (result.kind !== "loaded") setError(result.message);
      setLoading(false);
    },
    [workspaceId],
  );

  const fetchStatus = useCallback(async (): Promise<boolean> => {
    const startedForWorkspaceId = workspaceId;
    const outcome = await loadGitHubStatus();
    if (workspaceIdRef.current !== startedForWorkspaceId) return false;
    if (outcome.kind === "reauth") {
      setRequiresCmmdAuth(true);
      setError(outcome.message);
      stopPolling();
      return false;
    }
    if (outcome.kind === "error") return false;
    setGithubLogin(outcome.githubLogin);
    setGithubConnected(outcome.connected);
    return outcome.connected;
  }, [stopPolling, workspaceId]);

  const startPollingForConnection = useCallback(() => {
    const startedForWorkspaceId = workspaceId;
    stopPolling();
    pollIntervalRef.current = setInterval(async () => {
      if (workspaceIdRef.current !== startedForWorkspaceId) return;
      const connected = await fetchStatus();
      if (workspaceIdRef.current !== startedForWorkspaceId) return;
      if (connected) {
        stopPolling();
        void fetchRepos(search);
      }
    }, 2000);
  }, [fetchStatus, fetchRepos, stopPolling, search, workspaceId]);

  const refetch = useCallback(() => {
    void fetchRepos(search);
  }, [fetchRepos, search]);

  // Initial load
  useEffect(() => {
    if (!enabled) {
      stopPolling();
      setLoading(false);
      return;
    }
    const startedForWorkspaceId = workspaceId;
    void (async () => {
      await fetchRepos(search);
      if (workspaceIdRef.current !== startedForWorkspaceId) return;
      const connected = await fetchStatus();
      if (workspaceIdRef.current !== startedForWorkspaceId) return;
      if (!connected) {
        startPollingForConnection();
      }
    })();
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, fetchRepos, fetchStatus, startPollingForConnection, stopPolling, workspaceId]);

  // Debounced re-fetch when search term changes
  useEffect(() => {
    if (!enabled) return;
    const timer = setTimeout(() => {
      void fetchRepos(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [enabled, search, fetchRepos]);

  return {
    repos,
    github_connected,
    github_login,
    warnings,
    loading,
    error,
    requiresCmmdAuth,
    refetch,
  };
}
